import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { GuestAuthResponse, GuestProfile } from '@pms/shared';
import { getToken, getRefreshToken, setToken, clearToken } from '../lib/auth';
import { getMe, refreshTokens, logoutRequest } from '../services/api/auth.api';

interface GuestAuthValue {
  account: GuestProfile | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (tokens: GuestAuthResponse) => Promise<void>;
  logout: () => Promise<void>;
}

const GuestAuthContext = createContext<GuestAuthValue | undefined>(undefined);

/**
 * Guest session state for the storefront. Hydrates /me on load; on an expired access
 * token, tries a single refresh before giving up. Tokens live in the isolated
 * bestacco_guest_* keys (lib/auth.ts) — never staff keys.
 */
export function GuestAuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<GuestProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getToken()) { setLoading(false); return; }
      try {
        const me = await getMe();
        if (!cancelled) setAccount(me);
      } catch {
        // Access token likely expired — try one refresh, then retry /me.
        const rt = getRefreshToken();
        if (rt) {
          try {
            const tokens = await refreshTokens(rt);
            setToken(tokens);
            const me = await getMe();
            if (!cancelled) setAccount(me);
          } catch {
            clearToken();
          }
        } else {
          clearToken();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function login(tokens: GuestAuthResponse) {
    setToken(tokens);
    try {
      setAccount(await getMe());
    } catch {
      /* leave unauthenticated if hydrate fails */
    }
  }

  async function logout() {
    const rt = getRefreshToken();
    if (rt) { try { await logoutRequest(rt); } catch { /* best-effort */ } }
    clearToken();
    setAccount(null);
  }

  return (
    <GuestAuthContext.Provider value={{ account, isAuthenticated: !!account, loading, login, logout }}>
      {children}
    </GuestAuthContext.Provider>
  );
}

export function useGuestAuth(): GuestAuthValue {
  const ctx = useContext(GuestAuthContext);
  if (!ctx) throw new Error('useGuestAuth must be used within GuestAuthProvider');
  return ctx;
}
